import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api"

// 📌 Fetch All Businesses with Pagination & Search
export const fetchBusinesses = createAsyncThunk(
  "business/fetchAll",
  async ({ page = 1, limit = 10, search = "" }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/businesses?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`
      );
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: "Something went wrong" }
      );
    }
  }
);

// 📌 Fetch Businesses (Filtered by Search & Category with Pagination)
export const fetchBusinessesFiltered = createAsyncThunk(
  "business/fetchFiltered",
  async ({ page = 1, limit = 10, search = "", categoryId = "" }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        page,
        limit,
        ...(search ? { search } : {}),
        ...(categoryId ? { categoryId } : {}),
      });
      const { data } = await api.get(`/filter-business/search?${params.toString()}`);
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: "Something went wrong" }
      );
    }
  }
);

// 📌 Fetch Businesses by User ID
export const fetchBusinessesByUserId = createAsyncThunk(
  "business/fetchByUserId",
  async (userId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/business/user/${userId}`, {
        withCredentials: true,
      });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Something went wrong" });
    }
  }
);

// 📌 Fetch Businesses by Category with Pagination
export const fetchBusinessesByCategory = createAsyncThunk(
  "business/fetchByCategory",
  async ({ categoryId, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/business-by-category/${categoryId}?page=${page}&limit=${limit}`
      );
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Something went wrong" });
    }
  }
);

// 📌 Fetch Single Business
export const fetchBusinessById = createAsyncThunk(
  "business/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/single/business/${id}`);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Something went wrong" });
    }
  }
);

// 📌 Add Business
export const addBusiness = createAsyncThunk(
  "business/add",
  async (formData, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/add-business", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Something went wrong" });
    }
  }
);

// 📌 Update Business
export const updateBusiness = createAsyncThunk(
  "business/update",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/update/business/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Something went wrong" });
    }
  }
);

// 📌 Delete Business
export const deleteBusiness = createAsyncThunk(
  "business/delete",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(`/delete/business/${id}`);
      return { ...data, id };
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Something went wrong" });
    }
  }
);

// 📌 Increment Views
export const incrementBusinessViews = createAsyncThunk(
  "business/incrementViews",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/business-views/${id}`);
      return { ...data, id };
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Something went wrong" });
    }
  }
);

// 📌 Fetch Business Stats
export const fetchBusinessStats = createAsyncThunk(
  "business/fetchStats",
  async (businessId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/business-stats/${businessId}`);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Something went wrong" });
    }
  }
);

const businessSlice = createSlice({
  name: "business",
  initialState: {
    businesses: [],
    selectedBusiness: null,
    businessStats: null, // ✅ store stats here
    loading: false,
    error: null,
    success: null,
    pagination: {
      total: 0,
      page: 1,
      totalPages: 0,
      limit: 10,
    },
    search: "",
  },
  reducers: {
    clearBusinessMessages: (state) => {
      state.error = null;
      state.success = null;
    },
    setBusinessSearch: (state, action) => {
      state.search = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // 📌 Fetch All
      .addCase(fetchBusinesses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBusinesses.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.businesses = action.payload.data.data;
          state.pagination = {
            total: action.payload.data.total,
            page: action.payload.data.page,
            totalPages: action.payload.data.totalPages,
            limit: action.payload.data.limit,
          };
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(fetchBusinesses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
      })

      // 📌 Fetch Filtered
      .addCase(fetchBusinessesFiltered.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBusinessesFiltered.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          const { total, page, pages, businesses } = action.payload.data;
          state.filteredBusinesses = businesses || [];
          state.pagination = {
            total: total || 0,
            page: page || 1,
            totalPages: pages || 1,
            limit: state.pagination.limit || 10,
          };
        } else {
          state.error = action.payload.message || "Failed to load businesses";
        }
      })
      .addCase(fetchBusinessesFiltered.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to load businesses";
      })

      // 📌 Fetch by User ID
      .addCase(fetchBusinessesByUserId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBusinessesByUserId.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.businesses = action.payload.data;
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(fetchBusinessesByUserId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
      })

      // 📌 Fetch by Category
      .addCase(fetchBusinessesByCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBusinessesByCategory.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.businesses = action.payload.data.businesses;
          state.pagination = {
            total: action.payload.data.total,
            page: action.payload.data.page,
            totalPages: action.payload.data.totalPages,
            limit: state.pagination.limit,
          };
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(fetchBusinessesByCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
      })

      // 📌 Fetch By ID
      .addCase(fetchBusinessById.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.selectedBusiness = action.payload.data;
        } else {
          state.error = action.payload.message;
        }
      })

      // 📌 Add
      .addCase(addBusiness.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.businesses.unshift(action.payload.data);
          state.success = action.payload.message;
        } else {
          state.error = action.payload.message;
        }
      })

      // 📌 Update
      .addCase(updateBusiness.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.businesses = state.businesses.map((b) =>
            b.businessId === action.payload.data.businessId ? action.payload.data : b
          );
          state.success = action.payload.message;
        } else {
          state.error = action.payload.message;
        }
      })

      // 📌 Delete
      .addCase(deleteBusiness.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.businesses = state.businesses.filter((b) => b.businessId !== action.payload.id);
          state.success = action.payload.message;
        } else {
          state.error = action.payload.message;
        }
      })

      // 📌 Increment Views
      .addCase(incrementBusinessViews.fulfilled, (state, action) => {
        if (action.payload.success) {
          const business = state.businesses.find((b) => b.businessId === action.payload.id);
          if (business) {
            business.listingViews = action.payload.data.listingViews;
          }
        }
      })

      // 📌 Fetch Business Stats
      .addCase(fetchBusinessStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBusinessStats.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.businessStats = action.payload.data;
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(fetchBusinessStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
      });
  },
});

export const { clearBusinessMessages, setBusinessSearch, setBusinessPage } = businessSlice.actions;
export default businessSlice.reducer;
